import React, { useState, useEffect, useCallback } from "react";
import { CUST, icons } from "../helpers/constants";
import { Trans, useTranslation } from "react-i18next";
import * as request from "../services/FetchService";
import { useIsMobile } from "../helpers/customHooks";

import Modify from "../components/Subscription/Modify";
import { motion } from "framer-motion";
import { useSidebarContext } from "../components/Sidebar/SiderbarContext";
import ActivatingNew from "../components/Subscription/ActivatingNew";
import DeviceStatuses from "../components/Subscription/DeviceStatuses";
import EditCardNew from "../components/Subscription/EditCardNew";
import toast from "react-hot-toast";
import NewButton from "../components/Buttons/NewButton";
import { useStripe } from "@stripe/react-stripe-js";
import Payments from "../components/Subscription/Payments";
import DeactivatingNew from "../components/Subscription/DeactivatingNew";
import ModifyNew from "../components/Subscription/ModifyNew";
import { useParams } from "react-router-dom";
import { useContextData } from "../components/ContextData";
import { FLAVOR } from "../helpers/constants";


const SubscriptionsNew = (props) => {
	const MOBILE = useIsMobile();
	const { accessToken, setAccessToken } = useContextData();

	const params = useParams();
	
	const { t } = useTranslation();
	const stripe = useStripe();

	const [receipts, setReceipts] = useState([]);
	const [cards, setCards] = useState([]);
	const [provider, setProvider] = useState("stripe");
	const [priceplans, setPriceplans] = useState(null);

	const [devices, setDevices] = useState([]);
	const [activeDevices, setActiveDevices] = useState([]);
	const [inactiveDevices, setInactiveDevices] = useState([]);

	const [loading, setLoading] = useState(true);
	const [secret, setSecret] = useState();
	const [failedPayments, setFailedPayments] = useState([]);
	const [invoiceInfo, setInvoiceInfo] = useState(null);

	const [selected, setSelected] = useState(null);
	const [backClicked, setBackClicked] = useState(false);

	const [activating, setActivating] = useState(false);
	const [deactivating, setDeactivating] = useState(false);
	const [modify, setModify] = useState(false);
	const [editCard, setEditCard] = useState(false);
	const [retrying, setRetrying] = useState(false);
	const [processing, setProcessing] = useState(false)

	const { topbarContent, setTopbarContent, topbarTwoContent, setTopbarTwoContent } = useSidebarContext();


	useEffect(() => {
		
		return() => {
			setTopbarTwoContent(null);
		}
	}, [])
	// -------------------------
	// Fetch primitives (NO cross-calls)
	// -------------------------
	const fetchReceipts = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/receipts");
		setReceipts(response?.data?.receipts ?? []);
	}, []);

	const fetchFailed = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/attention");
		const data = response?.data;
		setFailedPayments(data?.subscriptions ?? []);
	}, []);

	const fetchDevices = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/getalldevices");
		const devs = response?.data?.devices ?? [];

		setDevices(devs);
		setInactiveDevices(devs.filter((d) => d?.dataplan_status === "inactive"));
		setActiveDevices(devs.filter((d) => d?.dataplan_status === "active"));
		return devs;
	}, []);

	const fetchCards = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/cards/list");
		const newCards = response?.data?.cards ?? [];
		setCards(newCards);
		return newCards;
	}, []);

	const fetchSecret = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/cards/get_client_secret");
		const s = response?.data?.client_secret;
		setSecret(s);
		return s;
	}, []);

	const fetchPriceplans = useCallback(async () => {
		const response = await request.getSubscription("payments/dataplan/priceplans");
		const data = response?.data;

		const allowed = data?.allowed_provider ?? "stripe";
		setProvider(allowed);
		setPriceplans(data?.priceplans ?? null);

		if (allowed === "stripe") {
			setInvoiceInfo(null);
		} else {
			setInvoiceInfo(data?.customer_info?.[0] ?? null);
			setCards([]); // optional: keep UI consistent when non-stripe
		}

		return data;
	}, []);

	// -------------------------
	// Orchestrators (safe refetch helpers)
	// -------------------------
	const init = useCallback(async () => {
		setLoading(true);
		toast.loading(t("subscription.fetching_data"), { id: "initial_load" });
		try {
			const priceplanData = await fetchPriceplans();

			await Promise.all([fetchReceipts(), fetchDevices(), fetchFailed()]);

			if (priceplanData?.allowed_provider === "stripe") {
				//await Promise.all([fetchSecret(), fetchCards()]);
				await fetchCards();

			}
		} catch (e) {
			console.error(e);
		} finally {
			toast.dismiss("initial_load")
			setLoading(false);
		}
	}, [fetchPriceplans, fetchReceipts, fetchDevices, fetchFailed, fetchCards]);

	const refetchAfterPlanChange = useCallback(async () => {
		// activation/deactivation/modify affects devices + receipts + failed state
		await Promise.all([fetchDevices(), fetchReceipts(), fetchFailed()]);
	}, [fetchDevices, fetchReceipts, fetchFailed]);

	const refetchCardsRelated = useCallback(async () => {
		if (provider !== "stripe") return;
		// after add/delete/update card
		await Promise.all([fetchCards(), fetchPriceplans()]);
	}, [provider, fetchCards, fetchPriceplans]);

	// -------------------------
	// Mount init
	// -------------------------
	useEffect(() => {
		init();
	}, []);

	// -------------------------
	// Actions
	// -------------------------
	const deleteCard = async (card_id) => {
		const obj = { payment_method: card_id };
		try {
			const resp = await request.postSubscription("", "payments/dataplan/cards/delete", obj);
			toast.success(t("toast.success"));
			await refetchCardsRelated();
		} catch (error) {
			toast.error(String(error));
		}
	};

	const handleDone = async (reroute = true) => {
		await refetchAfterPlanChange();
		if(reroute){
			setSelected("statuses");
		}
	};

	const flowFinsished = () => {
		setModify(false);
		setActivating(false);
	};

	const backClick = () => {
		if (selected === "activate" || selected === "modify") {
			setBackClicked(true);
			return;
		}
		setSelected(null);
	};

	// -------------------------
	// Topbar sync
	// -------------------------
	useEffect(() => {
		if (!selected) {
			setTopbarContent(null);
			setTopbarTwoContent(null);
			return;
		}

		if (MOBILE) {
			const topcontent = {
				left: (
					<div className={`${processing ? "opacity-25" : "cursor-pointer"}  round-bg bg-white menu-button d-flex align-items-center justify-content-center`} onClick={backClick}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
				),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0 text-truncate w-50 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t(`subscription.${selected}`)}
					</motion.h4>
				),
			};

			setTopbarContent((prev) => ({ ...(prev ?? {}), ...topcontent }));
			setTopbarTwoContent(null);
			return;
		}

		// Desktop
		const topcontent = {
			center: (
				<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0 text-truncate w-50 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
					{t("subscription.subscription")}
				</motion.h4>
			),
		};

		const topTwoContent = {
			left: (
				<div className="d-flex align-items-center">
					<div className={`${processing ? "opacity-25" : "cursor-pointer"} round-bg bg-white menu-button d-flex align-items-center justify-content-center`} onClick={backClick}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
					<motion.h4 key="title" className="ms-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t(`subscription.${selected}`)}
					</motion.h4>
				</div>
			),
			right: null,
		};

		setTopbarContent((prev) => ({ ...(prev ?? {}), ...topcontent }));
		setTopbarTwoContent((prev) => ({ ...(prev ?? {}), ...topTwoContent }));
	}, [selected, MOBILE, t, setTopbarContent, setTopbarTwoContent, processing]);

	const openEditCard = async() => {
		if(provider !== "stripe") return;

		try{
			await fetchSecret()
			setSelected("edit_card")
		}catch(error){
			toast.error(error)
		}
	}

	const items = [
		{
			key: "edit_card",
			label: t("subscription.edit_card"),
			icon: icons.chevron_right,
			function: openEditCard,
			hidden: cards.length === 0 || provider === "invoice",
		},
		{
			key: "payments",
			label: t("subscription.payments"),
			icon: icons.chevron_right,
			function: () => setSelected("payments"),
			hidden: provider === "invoice"
		},
		{
			key: "statuses",
			label: t("subscription.device_statuses"),
			icon: icons.chevron_right,
			function: () => setSelected("statuses"),
		},
		// {
		// 	key: "deactivate",
		// 	label: t("subscription.deactivate_dataplans"),
		// 	icon: icons.chevron_right,
		// 	function: () => setSelected("deactivate"),
		// },
	];

	const durationLabel = (duration) => {
		switch (Number(duration)) {
			case 1:
				return t("subscription.monthly");
			case 12:
				return t("subscription.yearly");
			default:
				return t("subscription.duration_months", { count: Number(duration) });
		}
	};

	const paymentLabel = (duration) => {
		switch (Number(duration)) {
			case 1:
				return t("subscription.billed_monthly");
			case 12:
				return t("subscription.billed_yearly");
			default:
				return t("subscription.duration_months", { count: Number(duration) });
		}
	};

	const failedReason = (reason) => {
		switch(reason){
			case "insufficient_funds": return(
				<Trans
					i18nKey={`subscription.failed_insufficient_funds`}
					components={{ reason: <span className="bold"/> }}
				/>
			)
			case "lost_card":
			case "stolen_card":
			case "expired_card":
			case "restricted_card":
			case "card_not_supported": return(
				<Trans
					i18nKey={`subscription.failed_card`}
					components={{ reason: <span className="bold"/> }}
				/>
			)
			default: return(
			<Trans
				i18nKey="subscription.failed_generic"
				components={{ reason: <span className="bold"/> }}
			/>
			)
		}
	}

	const deactivatePayment = async(intent) => {
		const obj = { payment_intent: intent };
		try {
			setDeactivating(true)
			toast.loading(t("subscription.processing"), { id: "retry_fail" });
			const resp = await request.postSubscription("", "payments/dataplan/cancel/pending_failed", obj);
			if(resp?.data?.error){
				toast.error(resp?.data?.message)
			}else{
				toast.success(t("toast.success"));
			}
			toast.dismiss("retry_fail");
			setDeactivating(false)
			await handleDone();
		} catch (error) {
			setDeactivating(false)
			toast.dismiss("retry_fail");
			toast.error(String(error));
		}
	}

	const retryCharge = async(intent) => {
		const obj = { payment_intent: intent };
		try {
			setRetrying(true)
			toast.loading(t("subscription.processing"), { id: "retry_charge" });
			const resp = await request.postSubscription("", "payments/dataplan/retry", obj);
			const waitForBackend = (ms) => new Promise((resolve) => setTimeout(resolve, ms));


			if (resp?.data?.status === "requires_action") {
				if(!stripe) return;
				const { error, paymentIntent: confirmed } = await stripe.confirmCardPayment(resp?.data?.client_secret);
				if (error) {
					toast.error(error.message);
					setRetrying(false)
					toast.dismiss("retry_charge");
					return;
				}

				if (confirmed?.status !== "succeeded" && confirmed?.status !== "processing") {
					toast.error(`Payment status: ${confirmed?.status || "unknown"}`);
					await handleDone(false);
					setRetrying(false)
					toast.dismiss("retry_charge");
					return;
				}
				await waitForBackend(1200);
				await handleDone();
				setRetrying(false)
				toast.success(t("toast.success"));
				toast.dismiss("retry_charge");
				return;
			}
			if(resp?.data?.status === "error"){
				toast.error(resp?.data?.message)
			}else{
				toast.success(t("toast.success"));
			}
			await waitForBackend(1200);
			toast.dismiss("retry_charge");
			setRetrying(false)
			await handleDone(false);

		} catch (error) {
			setRetrying(false)
			toast.dismiss("retry_charge");
			toast.error(String(error));
		}
	}

	const deactivatePlan = async(deviceid) => {
		try {
			toast.loading(t("subscription.processing"), { id: "deactivating" });
			const resp = await request.postSubscription("", "payments/dataplan/cancel/active_pending", { deviceid });
			if(!resp?.data?.successful){
				toast.error(resp?.data?.message)
			}else{
				toast.success(t("toast.success"));
			}
			toast.dismiss("deactivating");
			await handleDone();
		} catch (error) {
			toast.dismiss("deactivating");
			toast.error(String(error));
		}
	}

	const undoCancel = async(deviceid) => {
		try {
			toast.loading(t("subscription.processing"), { id: "reactivating" });
			const resp = await request.postSubscription("", "payments/dataplan/cancel/undo", { deviceid });
			if(resp?.data?.error){
				toast.error(resp?.data?.message)
			}else{
				toast.success(t("toast.success"));
			}
			toast.dismiss("reactivating");
			await handleDone();
		} catch (error) {
			toast.dismiss("reactivating");
			toast.error(String(error));
		}
	}

	const undoSchedule = async(deviceids, duration, dataplan) => {
		try {
			toast.loading(t("subscription.processing"), { id: "reactivating" });
			const resp = await request.postSubscription("", "payments/dataplan/update/", { deviceids, duration, dataplan });
			if(!resp?.data?.successful){
				toast.error(resp?.data?.message)
			}else{
				toast.success(t("toast.success"));
			}
			toast.dismiss("reactivating");
			await handleDone();
		} catch (error) {
			toast.dismiss("reactivating");
			toast.error(String(error));
		}
	}

	const contactSupport = () => {
		window.location.href = `
			mailto:support@swetrack.com?
			subject=Subscription related question`;
	}

	const mainListContent = () => {
		if (MOBILE && selected) return null;

		return (
			
			<div className="info-section-nomap section-divider position-relative scrollable-y grey-blue padding-bottom-20">
				<div className="margin-x-20">
					{(failedPayments?.length > 0 ?? 0) ? (
						<>
						<p className="mb-1 bold mt-3">{t("subscription.failed_charge")}</p>
							{failedPayments?.map((payment, index) => {

								const reason =
									payment?.requires_action_reason ??
									payment?.failure?.decline_code ??
									"";
								return(
									<div key={index} className="d-flex flex-column settings-radius mb-3">

									<div className="bg-white px-3 py-3 d-flex flex-column position-relative">
										<div className="d-flex align-items-center">
											<div className="full-rounded bg-red p-2 m-0 p-0 d-flex justify-content-center align-items-center me-3">
												<img src={icons.close} alt="close" className="white-white-icon size_15" />
											</div>
											<p>{failedReason(reason)}</p>
										</div>
										<hr className='m-0 p-0 event-divider bg-light-grey my-3' />
										<h4>{t("subscription.payment_summary")}</h4>
										<div className="d-flex mt-3 gap-3"> 
											<div className="d-flex flex-column">
												<p className="body dark-grey">{t("subscription.devices")}</p>
												<p className="body bold">{`${payment?.devices?.length ?? 0} ${t("subscription.devices", { count: payment?.devices?.length })}`}</p>
											</div>
											<div className="d-flex flex-column">
												<p className="body dark-grey">{t("subscription.period")}</p>
												<p className="body bold">{durationLabel(payment?.duration)}</p>
											</div>
										</div>
										<div className={`d-flex flex-column align-items-start me-3 mt-3`}>
											<p className="align-self-start body dark-grey">{t("subscription.total")}:</p>
											<div className="d-flex">
												<h2>{(payment?.amount_inc_vat ?? 0) / 100}</h2>
												{"\u00A0"}
												<h4 className="align-self-end mb-1">{payment?.currency?.toUpperCase()}</h4>
											</div>
											<p className="dark-grey">{paymentLabel(payment?.duration)}</p>
										</div>
							
										<div className="d-flex justify-content-between gap-3 mt-3">
											<NewButton className={`bg-red white-white bold p-3 rounded24 w-50`} onClick={() => deactivatePayment(payment?.payment_intent)}>
												{t("subscription.cancel")}
											</NewButton>
												{cards?.length === 0 && provider === "stripe" ?
													(
														<NewButton className={`bg-primary white-white bold p-3 rounded24 w-50`} onClick={openEditCard}>
															{retrying && <span className="me-2 size_15 spinner-grow"/>}
															{t("subscription.add_card")}
														</NewButton>
													)
													:
													(
														<NewButton className={`bg-gradient white-white bold p-3 rounded24 w-50`} onClick={() => retryCharge(payment?.payment_intent)}>
															{retrying && <span className="me-2 size_15 spinner-grow"/>}
															{t("subscription.retry")}
														</NewButton>
													)
												}
										</div>
									</div>
									</div>

								)
							})}
						</>
					) : (
						<>
							{cards?.length === 0 && provider === "stripe" ? (
								<div className="d-flex flex-column settings-radius mt-3">
									<div className="bg-grey-blue px-3 py-5 d-flex flex-column justify-content-center align-items-center white position-relative cursor-pointer" onClick={openEditCard}>
										<h4>{t("subscription.add_card")}</h4>
										<p>{t("subscription.add_card_desc")}</p>
										<div className="round-bg bg-white p-1 d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 me-3 mb-3">
											<img src={icons.chevron_right} alt="close" className="size_12 primary-icon" />
										</div>
									</div>
								</div>
							) : (
								provider !== "stripe"
								?
									<div className="d-flex flex-column settings-radius mt-3">
										<div className="bg-gradient px-3 py-5 d-flex flex-column justify-content-center align-items-center white-white position-relative cursor-pointer" onClick={() => contactSupport()}>
											<h4>{t("subscription.contact_support")}</h4>
											<p>{t("subscription.contact_support_sub")}</p>
											<div className="round-bg bg-white p-1 d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 me-3 mb-3">
												<img src={icons.chevron_right} alt="close" className="size_12 primary-icon" />
											</div>
										</div>
									</div>
								:
								<>
									<div className="d-flex flex-column settings-radius mt-3">
										<div className="bg-gradient px-3 py-5 d-flex flex-column justify-content-center align-items-center white-white position-relative cursor-pointer" onClick={() => setSelected("activate")}>
											<h4>{t("subscription.activate")}</h4>
											<p>{t("subscription.new_dataplan")}</p>
											<div className="round-bg bg-white p-1 d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 me-3 mb-3">
												<img src={icons.chevron_right} alt="close" className="size_12 primary-icon" />
											</div>
										</div>
									</div>
									{activeDevices?.length > 0 && (CUST === "swetrack" || CUST === "livelo") &&
										<div className="d-flex flex-column settings-radius mt-3">
											<div className="bg-grey-blue px-3 py-5 d-flex flex-column justify-content-center align-items-center white position-relative cursor-pointer" onClick={() => setSelected("modify")}>
												<h4>{t("subscription.modify")}</h4>
												<p>{t("subscription.current_dataplan")}</p>
												<div className="round-bg bg-white p-1 d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 me-3 mb-3">
													<img src={icons.chevron_right} alt="close" className="size_12 primary-icon" />
												</div>
											</div>
										</div>
									}
								</>
							)}
						</>
					)}

					<p className="mb-1 bold mt-3">{t("subscription.details")}</p>

					<div className="d-flex flex-column settings-radius">
						{items.map((item, index) => !item.hidden && (
							<div onClick={() => item.function(item.key)} key={index} className={`cursor-pointer p-3 d-flex justify-content-between align-items-center ${selected === item.key ? "bg-grey-blue white" : "bg-white grey-blue"} ${index < items.length - 1 ? "border-settings" : ""}`}>
								<span className={`body ${item.destructive ? "red" : ""}`}>{item.label}</span>
								<img className={`size_15 ${selected === item.key ? "white-icon" : "primary-icon"}`} src={item.icon} alt="" />
							</div>
						))}
					</div>
				</div>
			</div>
		);
	};

	const renderSelectedContent = (type) => {
		switch (type) {
			case "activate":
				return (
						<ActivatingNew
							stripe={stripe}
							cards={cards}
							provider={provider}
							priceplans={priceplans}
							deleteCard={deleteCard}
							onBackFromStepOne={() => setSelected(null)}
							backClicked={backClicked}
							setBackClicked={setBackClicked}
							show={selected === "activate"}
							onClose={() => setSelected(null)}
							finalize={() => flowFinsished()}
							onBackFromDone={handleDone}
							devices={inactiveDevices}
							invoiceInfo={invoiceInfo}
							setProcessing={setProcessing}
						/>
				);

			case "edit_card":
				return (
					<EditCardNew
						cards={cards}
						getCards={refetchCardsRelated} // ✅ no loop: this only refetches cards/secret
						deleteCard={deleteCard}
						clientSecret={secret}
					/>
				);

			case "modify":
				return (
					<ModifyNew
						priceplans={priceplans}
						provider={provider}
						onBackFromStepOne={() => setSelected(null)}
						onBackFromDone={handleDone}
						backClicked={backClicked}
						setBackClicked={setBackClicked}
						devices={activeDevices}
						invoiceInfo={invoiceInfo}
						setProcessing={setProcessing}
					/>
				);

			case "deactivate":
				return (
					<DeactivatingNew
						devices={activeDevices}
						finalize={async () => {
							flowFinsished();
							await refetchAfterPlanChange();
						}}
					/>
				);
			case "payments":
				return (
					<Payments receipts={receipts ?? []} />
				)

			case "statuses":
				return <DeviceStatuses 
				devices={devices}
				deactivatePlan={deactivatePlan}
				undoCancel={undoCancel}
				undoSchedule={undoSchedule}
				provider={provider}
				/>;

			default:
				return null;
		}
	};

	if (loading) return;
	return (
		<>
			{mainListContent()}
			<div className={`${!selected ? "d-none" : ""} settings-right-section scrollable-y`}>{renderSelectedContent(selected)}</div>

			{/* <EditCardModal show={editCard} cards={cards} updateCards={refetchCardsRelated} deleteCard={(data) => deleteCard(data)} onClose={() => setEditCard(!editCard)} /> */}
		</>
	);
};

export default SubscriptionsNew;
